import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { uploadWorkspaceArtifact } from './storageHubService'
import type { ChecklistAttachment, ChecklistProofKind } from '../types/schema'

export const MAX_CHECKLIST_COMMENT_ATTACHMENTS = 5
export const MAX_CHECKLIST_ATTACHMENT_BYTES = 50 * 1024 * 1024

type UploadIdentity = {
  userId: string
  userName: string
}

const uploadFile = async (file: File | Blob, workspaceId: string, artifactType: string, artifactId: string, identity: UploadIdentity, fallbackName: string) => {
  if (file.size > MAX_CHECKLIST_ATTACHMENT_BYTES) throw new Error('Attachments must be smaller than 50 MB.')
  const contentType = file.type || 'application/octet-stream'
  const name = file instanceof File && file.name ? file.name : fallbackName
  const uploadFileValue = file instanceof File ? file : new File([file], name, { type: contentType })
  const uploaded = await uploadWorkspaceArtifact({ workspaceId, file: uploadFileValue, artifactType, artifactId })
  return {
    id: uploaded.fileId,
    name,
    contentType,
    size: file.size,
    downloadUrl: uploaded.downloadUrl,
    storagePath: uploaded.storagePath,
    uploadedById: identity.userId,
    uploadedByName: identity.userName,
    uploadedAt: new Date().toISOString(),
  } satisfies ChecklistAttachment
}

export const uploadChecklistCommentAttachments = async (input: {
  workspaceId: string
  checklistItemId: string
  commentId: string
  files: File[]
  identity: UploadIdentity
}) => {
  if (input.files.length > MAX_CHECKLIST_COMMENT_ATTACHMENTS) throw new Error('A comment can include up to 5 attachments.')
  return Promise.all(input.files.map((file, index) => {
    const attachmentId = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
    return uploadFile(file, input.workspaceId, 'CHECKLIST_COMMENT_ATTACHMENT', `${input.checklistItemId}:${input.commentId}:${attachmentId}`, input.identity, 'attachment')
  }))
}

export const uploadChecklistProof = async (input: {
  workspaceId: string
  checklistItemId: string
  pageKey?: string | null
  file: File | Blob
  fileName: string
  kind: ChecklistProofKind
  identity: UploadIdentity
}) => {
  const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  const attachment = await uploadFile(input.file, input.workspaceId, `CHECKLIST_${input.kind}`, `${input.checklistItemId}:${submissionId}`, input.identity, input.fileName)
  const submissionRef = doc(collection(firestoreDb, 'checklist_proof_submissions'))
  await setDoc(submissionRef, {
    ...attachment,
    proofSubmissionId: submissionRef.id,
    workspaceId: input.workspaceId,
    checklistItemId: input.checklistItemId,
    pageKey: input.pageKey ?? null,
    proofPeriodKey: input.pageKey ?? null,
    kind: input.kind,
    createdAt: serverTimestamp(),
  })
  return { ...attachment, proofSubmissionId: submissionRef.id }
}
